/**
 * Single-page checkout — Shopify-inspired with structured address fields.
 *
 * Sections (one page, no steps):
 *   1. Contacto: Nombre completo, Teléfono, Correo electrónico (all required).
 *   2. Dirección de envío: Calle y número, Depto/Suite (opcional), Ciudad,
 *      Estado, Código Postal.
 *   3. Resumen del pedido (sticky right, collapsible on mobile) with inline
 *      qty steppers and a big CTA "🔒 Pagar $X MXN →".
 *
 * Pickup option was removed; the only delivery method is home shipping.
 * Personal data + address are forwarded to Mercado Pago as `payer` so the
 * customer doesn't retype anything in the MP pasarela.
 */
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, MapPin, Spinner, Lock, CaretDown, CaretUp,
  Trash, Minus, Plus, User, ShieldCheck, CheckCircle, XCircle, Clock, WhatsappLogo,
} from "@phosphor-icons/react";
import { initMercadoPago, Payment, StatusScreen, usePaymentBrick } from "@mercadopago/sdk-react";

// Constante fuera del componente: si cambia la referencia, el SDK destruye y
// vuelve a crear el Brick (y el cliente pierde lo que ya escribió).
const BRICK_CUSTOMIZATION = {
  paymentMethods: {
    creditCard: "all",
    debitCard: "all",
  },
  visual: { style: { theme: "default" } },
};
import { useCart } from "@/context/CartContext";
import { quoteShipping, loadLocation } from "@/lib/shipping";

const API = process.env.REACT_APP_BACKEND_URL;
let mpInitialized = false;

const fmtMXN = (n) =>
  `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());
const isPhone = (s) => (s || "").replace(/\D/g, "").length >= 10;

function unitInMxn(item, fx) {
  if ((item.currency || "MXN").toUpperCase() === "USD") {
    return Number(item.unit_price || 0) * Number(fx || 17);
  }
  return Number(item.unit_price || 0);
}

const STORAGE_KEY = "alendra_checkout_form_v2";
const initialForm = {
  name: "", phone: "", email: "",
  street: "", apt: "", city: "", state: "", zip: "",
};

function loadForm() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialForm;
    const parsed = JSON.parse(raw);
    return { ...initialForm, ...parsed };
  } catch { return initialForm; }
}

export default function Checkout() {
  const { items, updateQty, removeItem, clear } = useCart();
  const navigate = useNavigate();

  const [mpReady, setMpReady] = useState(mpInitialized);
  const [config, setConfig] = useState({ shipping_cost_mxn: 150, exchange_rate: 17, company: null });
  const [form, setForm] = useState(loadForm);
  const [touched, setTouched] = useState({});
  const [paymentResult, setPaymentResult] = useState(null);
  const [error, setError] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const formValidRef = useRef(false);
  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, discount_amount_mxn, ...}
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/public/checkout/config`),
      axios.get(`${API}/api/public/branding`).catch(() => ({ data: null })),
    ]).then(([cfg, brand]) => {
      setConfig({ ...cfg.data, company: brand.data });
      // Initialize Mercado Pago SDK once with the public key from settings
      if (!mpInitialized && cfg.data?.public_key) {
        try {
          initMercadoPago(cfg.data.public_key, { locale: "es-MX" });
          mpInitialized = true;
        } catch (e) { /* already init */ }
      }
      setMpReady(mpInitialized);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  useEffect(() => {
    if (items.length === 0 && !paymentResult) {
      const t = setTimeout(() => navigate("/catalogo"), 1200);
      return () => clearTimeout(t);
    }
  }, [items.length, paymentResult, navigate]);

  const subtotalMxn = useMemo(
    () => items.reduce((s, i) => s + unitInMxn(i, config.exchange_rate) * i.qty, 0),
    [items, config.exchange_rate],
  );
  // Envío por zona (local gratis / Baja California / resto de México) según la dirección capturada.
  const [shipQuote, setShipQuote] = useState(null);
  useEffect(() => {
    if (items.length === 0) return;
    let loc = { state: form.state, city: form.city };
    if (!loc.state && !loc.city) loc = loadLocation() || loc;
    const t = setTimeout(() => {
      quoteShipping(items.map((i) => i.product_id), loc).then(setShipQuote).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => i.product_id).join(","), form.state, form.city]);
  useEffect(() => {
    const saved = loadLocation();
    if (saved && !form.city && !form.state) setForm((f) => ({ ...f, city: saved.city || "", state: saved.state || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const shipping = shipQuote ? Number(shipQuote.cost_mxn || 0) : Number(config.shipping_cost_mxn || 0);
  const shippingLabel = shipQuote ? `${shipQuote.zone_label}${shipQuote.city ? ` · ${shipQuote.city}` : ""}` : "";
  const discount = coupon?.discount_amount_mxn || 0;
  const totalMxn = Math.max(0, subtotalMxn - discount + shipping);

  // Al aprobarse el pago se vacía el carrito; congelamos lo comprado para que
  // el recibo muestre los artículos y el total real (no un carrito vacío).
  const [paidOrder, setPaidOrder] = useState(null);
  const freezePaidOrder = (serverTotal) => {
    setPaidOrder({
      items: items.map((i) => ({ ...i })),
      subtotalMxn, shipping, shippingLabel, coupon,
      totalMxn: Number.isFinite(Number(serverTotal)) && Number(serverTotal) > 0 ? Number(serverTotal) : totalMxn,
    });
  };
  const isPaid = paymentResult?.status === "paid";
  const view = isPaid && paidOrder
    ? paidOrder
    : { items, subtotalMxn, shipping, shippingLabel, coupon, totalMxn };

  // Re-validate coupon when subtotal changes (e.g., qty edit). If the
  // current discount is no longer valid (min subtotal not met), drop it.
  useEffect(() => {
    if (!coupon) return;
    const minOk = !coupon.min_subtotal_mxn || subtotalMxn >= coupon.min_subtotal_mxn;
    if (!minOk) {
      setCoupon(null);
      setCouponError("El cupón ya no aplica con el nuevo subtotal.");
    }
  }, [subtotalMxn, coupon]);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCouponLoading(true);
    setCouponError("");
    try {
      const { data } = await axios.post(`${API}/api/public/checkout/validate-coupon`, {
        code, subtotal_mxn: subtotalMxn,
        email: form.email.trim().toLowerCase() || null,
      });
      setCoupon(data);
      setCouponInput("");
    } catch (e) {
      setCouponError(e?.response?.data?.detail || "Cupón inválido");
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => { setCoupon(null); setCouponError(""); };

  // Validation
  const validations = {
    name: form.name.trim().length >= 3,
    phone: isPhone(form.phone),
    email: isEmail(form.email),
    street: form.street.trim().length >= 4,
    city: form.city.trim().length >= 2,
    state: form.state.trim().length >= 2,
    zip: /^\d{4,6}$/.test(form.zip.trim()),
  };
  const allValid = Object.values(validations).every(Boolean) && items.length > 0;
  // Sync ref so the Brick's onSubmit (captured closure) reads latest form state.
  useEffect(() => { formValidRef.current = allValid; }, [allValid]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const markTouched = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const vendorRef = (() => { try { return localStorage.getItem("alendra_referrer") || ""; } catch { return ""; } })();

  // El Brick se monta UNA sola vez: sus callbacks leen el estado más reciente
  // desde esta ref para no tener que recrearse (recrearlo borra la tarjeta).
  const latestRef = useRef({});
  latestRef.current = { items, form, coupon, vendorRef, totalMxn, freezePaidOrder, clear };

  // Brick's onSubmit — called when user completes the inline payment form.
  // formData contains: token, payment_method_id, installments, issuer_id, payer
  const handleBrickSubmit = useCallback(async ({ formData }) => {
    const { items, form, coupon, vendorRef, freezePaidOrder, clear } = latestRef.current;
    if (!formValidRef.current) {
      setTouched({ name: true, phone: true, email: true, street: true, city: true, state: true, zip: true });
      setError("Por favor completa todos los datos del contacto y la dirección antes de pagar.");
      throw new Error("missing_form");
    }
    setError("");
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty, kind: i.kind || "product" })),
        payer_name: form.name.trim(),
        payer_phone: form.phone.trim(),
        payer_email: form.email.trim().toLowerCase(),
        address_street: form.street.trim(),
        address_apt: form.apt.trim(),
        address_city: form.city.trim(),
        address_state: form.state.trim(),
        address_zip: form.zip.trim(),
        payment: formData,
        coupon_code: coupon?.code || null,
        vendor_ref: vendorRef || null,
        device_session_id: (typeof window !== "undefined" && window.MP_DEVICE_SESSION_ID) || null,
      };
      const { data } = await axios.post(`${API}/api/public/checkout/process-payment`, payload);
      if (data.status === "paid") freezePaidOrder(data.total_mxn);
      setPaymentResult(data);
      if (data.status === "paid") {
        clear();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (data.three_ds_info) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "No se pudo procesar el pago.";
      setError(String(msg));
      throw e; // re-throw so the Brick stays in its current state
    }
  }, []);

  const handleBrickError = useCallback((err) => {
    console.error("MP Brick error:", err);
    setError("Hubo un problema con el formulario de pago. Recarga la página.");
  }, []);

  // `initialization` se fija al montar (monto + correo); después sólo se
  // actualiza el monto vía update() para no destruir el Brick.
  const [brickInit, setBrickInit] = useState(null);
  useEffect(() => {
    if (!brickInit && mpReady && allValid && totalMxn > 0 && !paymentResult) {
      setBrickInit({ amount: totalMxn, payer: { email: form.email.trim().toLowerCase() } });
    }
  }, [brickInit, mpReady, allValid, totalMxn, paymentResult, form.email]);
  const { update: updateBrick } = usePaymentBrick();
  useEffect(() => {
    if (brickInit && totalMxn > 0 && totalMxn !== brickInit.amount) {
      try { updateBrick({ amount: totalMxn }); } catch { /* el Brick aún no está listo */ }
    }
  }, [brickInit, totalMxn, updateBrick]);

  // Gancho sólo en desarrollo para que las pruebas automatizadas disparen el
  // mismo camino que el Brick (que no tokeniza en el entorno de preview).
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return undefined;
    window.__qa_submit_payment = (formData) => handleBrickSubmit({ formData: formData || { token: "qa" } });
    return () => { delete window.__qa_submit_payment; };
  });

  if (items.length === 0 && !paymentResult) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-extrabold text-zinc-900 mb-2">Tu carrito está vacío</h1>
          <p className="text-zinc-500 mb-6">Te llevamos al catálogo…</p>
          <Link to="/catalogo" className="inline-flex items-center gap-2 px-5 py-3 bg-zinc-900 text-white rounded-full text-sm font-semibold">
            Volver al catálogo
          </Link>
        </div>
      </div>
    );
  }

  const company = config.company || {};
  const brandName = company.company_name || "Alendra PC";
  const brandLogo = company.company_logo || null;

  return (
    <div className="min-h-screen bg-white">
      {/* ============== HEADER ============== */}
      <header className="border-b border-zinc-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/catalogo" className="flex items-center gap-2.5" data-testid="checkout-logo">
            {brandLogo ? (
              <img src={brandLogo} alt={brandName} className="h-8 w-8 object-contain rounded" />
            ) : (
              <div className="h-8 w-8 rounded bg-[#002FA7] text-white font-bold flex items-center justify-center text-sm">A</div>
            )}
            <span className="font-heading font-extrabold text-lg tracking-tight text-zinc-900">{brandName}</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-mono text-zinc-500">
              <Lock size={12} weight="bold" className="text-emerald-600" /> Pago seguro
            </span>
            <Link to="/catalogo" className="text-xs text-zinc-500 hover:text-zinc-900 underline" data-testid="checkout-keep-shopping">
              Seguir comprando
            </Link>
          </div>
        </div>
      </header>

      {/* Mobile summary toggle */}
      <div className="lg:hidden bg-zinc-50 border-b border-zinc-100">
        <button
          type="button"
          onClick={() => setSummaryOpen((o) => !o)}
          className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between text-sm"
          data-testid="mobile-summary-toggle"
        >
          <span className="inline-flex items-center gap-2 text-zinc-600">
            {summaryOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
            {summaryOpen ? "Ocultar resumen del pedido" : "Mostrar resumen del pedido"}
          </span>
          <span className="font-heading text-xl font-extrabold text-zinc-900 numeric" data-testid="mobile-summary-total">{fmtMXN(view.totalMxn)}</span>
        </button>
        {summaryOpen && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
            <OrderSummary
              items={view.items} fx={config.exchange_rate}
              shipping={view.shipping} shippingLabel={view.shippingLabel} subtotalMxn={view.subtotalMxn} totalMxn={view.totalMxn}
              onUpdateQty={updateQty} onRemove={removeItem}
              coupon={view.coupon}
              readOnly={isPaid}
              compact
            />
          </div>
        )}
      </div>

      {/* ============== MAIN ============== */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 lg:py-12 grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-8 lg:gap-16">
        {/* ---------- LEFT: form (o recibo cuando el pago ya fue aprobado) ---------- */}
        {isPaid ? (
          <section className="space-y-6" data-testid="checkout-receipt">
            <PaymentResultCard result={paymentResult} supportWhatsapp={config.support_whatsapp} />
            <div className="bg-white border border-zinc-200 rounded-2xl p-5 text-sm text-zinc-700 space-y-2">
              <h3 className="font-heading text-base font-extrabold tracking-tight text-zinc-900">¿Qué sigue?</h3>
              <p>Te enviaremos la confirmación a <b className="text-zinc-900">{form.email}</b> y te contactaremos al <b className="text-zinc-900">{form.phone}</b> para coordinar la entrega.</p>
              {form.street && <p className="text-xs text-zinc-500">Envío a: {[form.street, form.apt, form.city, form.state, form.zip].filter(Boolean).join(", ")}</p>}
              <Link to="/catalogo" className="inline-flex items-center justify-center gap-2 mt-2 h-11 px-6 rounded-full bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800" data-testid="receipt-keep-shopping">
                Seguir comprando
              </Link>
            </div>
          </section>
        ) : (
        <section className="space-y-10">
          {/* CONTACTO */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <User size={16} weight="duotone" className="text-zinc-500" />
              <h2 className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-zinc-900">Contacto</h2>
            </div>
            <div className="space-y-3">
              <Field
                value={form.name}
                onChange={setField("name")}
                onBlur={markTouched("name")}
                placeholder="Nombre completo"
                autoComplete="name"
                required
                error={touched.name && !validations.name && "Ingresa tu nombre completo."}
                testid="checkout-name"
              />
              <Field
                value={form.phone}
                onChange={setField("phone")}
                onBlur={markTouched("phone")}
                placeholder="Teléfono (10 dígitos)"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                required
                error={touched.phone && !validations.phone && "Ingresa un teléfono de 10 dígitos."}
                testid="checkout-phone"
              />
              <Field
                value={form.email}
                onChange={setField("email")}
                onBlur={markTouched("email")}
                placeholder="Correo electrónico"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                error={touched.email && !validations.email && "Ingresa un correo válido."}
                hint="Te enviaremos el recibo y actualizaciones del pedido."
                testid="checkout-email"
              />
            </div>
          </div>

          {/* ENVÍO */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={16} weight="duotone" className="text-zinc-500" />
              <h2 className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-zinc-900">Dirección de envío</h2>
              <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                {shipping === 0 ? "envío GRATIS" : `${fmtMXN(shipping)} envío`}
              </span>
            </div>
            <div className="space-y-3">
              <Field
                value={form.street}
                onChange={setField("street")}
                onBlur={markTouched("street")}
                placeholder="Calle y número"
                autoComplete="address-line1"
                required
                error={touched.street && !validations.street && "Ingresa tu calle y número."}
                testid="checkout-street"
              />
              <Field
                value={form.apt}
                onChange={setField("apt")}
                placeholder="Depto, suite, colonia, referencias…"
                autoComplete="address-line2"
                optional
                testid="checkout-apt"
              />
              <div className="grid grid-cols-2 gap-3">
                <Field
                  value={form.city}
                  onChange={setField("city")}
                  onBlur={markTouched("city")}
                  placeholder="Ciudad"
                  autoComplete="address-level2"
                  required
                  error={touched.city && !validations.city && "Ciudad requerida."}
                  testid="checkout-city"
                />
                <Field
                  value={form.state}
                  onChange={setField("state")}
                  onBlur={markTouched("state")}
                  placeholder="Estado"
                  autoComplete="address-level1"
                  required
                  error={touched.state && !validations.state && "Estado requerido."}
                  testid="checkout-state"
                />
              </div>
              <Field
                value={form.zip}
                onChange={setField("zip")}
                onBlur={markTouched("zip")}
                placeholder="Código postal"
                inputMode="numeric"
                autoComplete="postal-code"
                required
                error={touched.zip && !validations.zip && "CP de 4 a 6 dígitos."}
                testid="checkout-zip"
              />
            </div>
          </div>

          <div className="hidden lg:flex items-start gap-2 text-xs text-zinc-500">
            <ShieldCheck size={14} weight="duotone" className="text-emerald-600 mt-0.5 shrink-0" />
            <span>Pagos con tarjeta de débito o crédito procesados de forma segura por Mercado Pago. Pre-llenamos tus datos para que sea instantáneo.</span>
          </div>
        </section>
        )}

        {/* ---------- RIGHT: order summary + payment brick ---------- */}
        <aside className="lg:sticky lg:top-8 lg:self-start space-y-4">
          <OrderSummary
            items={view.items} fx={config.exchange_rate}
            shipping={view.shipping} shippingLabel={view.shippingLabel} subtotalMxn={view.subtotalMxn} totalMxn={view.totalMxn}
            onUpdateQty={updateQty} onRemove={removeItem}
            coupon={view.coupon} couponInput={couponInput} setCouponInput={setCouponInput}
            applyCoupon={applyCoupon} removeCoupon={removeCoupon}
            couponLoading={couponLoading} couponError={couponError}
            readOnly={isPaid}
          />

          {/* Payment result (success/pending/rejected) or 3DS bank challenge */}
          {paymentResult?.three_ds_info && paymentResult.status === "pending" ? (
            <ThreeDSChallenge
              result={paymentResult}
              onResolved={(fresh) => {
                if (fresh.status === "paid") freezePaidOrder(paymentResult.total_mxn);
                setPaymentResult({ ...paymentResult, ...fresh, three_ds_info: null });
                if (fresh.status === "paid") { clear(); window.scrollTo({ top: 0, behavior: "smooth" }); }
              }}
            />
          ) : paymentResult && !isPaid && (
            <PaymentResultCard result={paymentResult} supportWhatsapp={config.support_whatsapp} />
          )}

          {/* Inline payment brick (credit + debit cards only) */}
          {!paymentResult && (
            <div className="bg-white border border-zinc-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-xs font-bold tracking-[0.2em] uppercase text-zinc-900">Método de pago</h2>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700">
                  <Lock size={10} weight="bold" /> 256-bit SSL
                </span>
              </div>

              {!allValid && (
                <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="brick-blocked">
                  Llena tus datos de contacto y dirección para activar el pago.
                </div>
              )}

              {error && (
                <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700" data-testid="checkout-error">
                  {error}
                </div>
              )}

              {brickInit ? (
                <div data-testid="mp-payment-brick" className={!allValid ? "opacity-50 pointer-events-none" : ""}>
                  <Payment
                    initialization={brickInit}
                    customization={BRICK_CUSTOMIZATION}
                    onSubmit={handleBrickSubmit}
                    onError={handleBrickError}
                  />
                </div>
              ) : (
                <div className="h-28 rounded-lg border border-dashed border-zinc-200 flex items-center justify-center text-xs text-zinc-400" data-testid="mp-payment-brick-placeholder">
                  {mpReady ? "El formulario de pago aparecerá al completar tus datos." : "Cargando pasarela de pago…"}
                </div>
              )}
            </div>
          )}
        </aside>
      </main>

      {/* Footer trust strip */}
      <footer className="mt-12 lg:mt-20 pb-28 lg:pb-12 border-t border-zinc-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-zinc-500">
          <div className="inline-flex items-center gap-2">
            <Lock size={12} weight="bold" className="text-emerald-600" />
            Pago procesado con cifrado 256-bit SSL
          </div>
          <div className="flex items-center gap-2 font-mono uppercase tracking-widest">
            <span>Powered by</span>
            <span className="font-bold text-[#009EE3]">Mercado Pago</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============================================================
// Subcomponents
// ============================================================
function Field({ value, onChange, onBlur, placeholder, type = "text", inputMode, autoComplete, required, optional, error, hint, testid }) {
  return (
    <label className="block relative">
      <input
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className={`w-full h-14 px-4 pr-20 text-base bg-white border rounded-xl outline-none transition-colors placeholder-zinc-400 ${
          error
            ? "border-red-400 focus:border-red-500"
            : "border-zinc-300 focus:border-zinc-900"
        }`}
        data-testid={testid}
      />
      <span className={`absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-mono uppercase tracking-widest pointer-events-none ${
        required ? "text-zinc-400" : "text-zinc-300"
      }`}>
        {required ? "Requerido" : optional ? "Opcional" : ""}
      </span>
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
      {!error && hint && <p className="mt-1.5 text-[11px] text-zinc-500">{hint}</p>}
    </label>
  );
}

function OrderSummary({ items, fx, shipping, shippingLabel, subtotalMxn, totalMxn, onUpdateQty, onRemove,
  coupon, couponInput, setCouponInput, applyCoupon, removeCoupon, couponLoading, couponError,
  compact = false, readOnly = false }) {
  return (
    <div className={`bg-zinc-50 rounded-2xl ${compact ? "p-4" : "p-5 sm:p-6"}`} data-testid={readOnly ? "order-summary-paid" : "order-summary"}>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-heading text-base font-extrabold tracking-tight text-zinc-900">{readOnly ? "Tu compra" : "Resumen"}</h2>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          {items.length} {items.length === 1 ? "artículo" : "artículos"}
        </span>
      </div>

      <ul className="space-y-4 max-h-[340px] overflow-y-auto pr-1">
        {items.map((it) => {
          const unitMxn = unitInMxn(it, fx);
          const lineMxn = unitMxn * it.qty;
          return (
            <li key={it.product_id} className="flex gap-3" data-testid={`summary-item-${it.product_id}`}>
              <div className="relative w-14 h-14 rounded-lg bg-white border border-zinc-200 overflow-hidden flex items-center justify-center shrink-0">
                {it.photo ? (
                  <img src={it.photo} alt={it.name} className="w-full h-full object-contain" loading="lazy" />
                ) : (
                  <span className="text-[9px] text-zinc-300">Sin foto</span>
                )}
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-zinc-700 text-white text-[10px] font-bold flex items-center justify-center">
                  {it.qty}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 leading-tight line-clamp-2">{it.name}</p>
                <p className="text-[11px] text-zinc-500 numeric mt-0.5">{fmtMXN(unitMxn)} c/u</p>
                {!readOnly && <div className="mt-1.5 flex items-center gap-1.5">
                  <button type="button" onClick={() => onUpdateQty(it.product_id, it.qty - 1)} className="w-6 h-6 inline-flex items-center justify-center rounded-md bg-white border border-zinc-200 hover:border-zinc-400 text-zinc-700" data-testid={`summary-minus-${it.product_id}`} aria-label="Disminuir">
                    <Minus size={10} weight="bold" />
                  </button>
                  <button type="button" onClick={() => onUpdateQty(it.product_id, it.qty + 1)} disabled={it.qty >= (it.max_qty || 1)} className="w-6 h-6 inline-flex items-center justify-center rounded-md bg-white border border-zinc-200 hover:border-zinc-400 text-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`summary-plus-${it.product_id}`} aria-label="Aumentar">
                    <Plus size={10} weight="bold" />
                  </button>
                  <button type="button" onClick={() => onRemove(it.product_id)} className="ml-1 text-zinc-400 hover:text-red-600 inline-flex items-center" data-testid={`summary-remove-${it.product_id}`} aria-label="Quitar">
                    <Trash size={11} weight="bold" />
                  </button>
                </div>}
              </div>
              <div className="text-sm font-semibold text-zinc-900 numeric whitespace-nowrap">{fmtMXN(lineMxn)}</div>
            </li>
          );
        })}
      </ul>

      <div className="mt-5 pt-4 border-t border-zinc-200 space-y-1.5 text-sm">
        <div className="flex justify-between text-zinc-600">
          <span>Subtotal</span>
          <span className="numeric" data-testid="summary-subtotal">{fmtMXN(subtotalMxn)}</span>
        </div>
        {coupon && (
          <div className="flex justify-between text-emerald-700 font-semibold">
            <span className="inline-flex items-center gap-1">
              🎟️ {coupon.code}
              {!readOnly && <button onClick={removeCoupon} className="ml-1 text-[10px] underline opacity-70 hover:opacity-100" data-testid="remove-coupon">quitar</button>}
            </span>
            <span className="numeric" data-testid="summary-discount">-{fmtMXN(coupon.discount_amount_mxn)}</span>
          </div>
        )}
        <div className="flex justify-between text-zinc-600">
          <span>Envío{shippingLabel ? <span className="block text-[10px] text-zinc-400 font-normal" data-testid="summary-shipping-zone">{shippingLabel}</span> : null}</span>
          <span className={`numeric ${shipping === 0 ? "text-emerald-700 font-semibold" : ""}`} data-testid="summary-shipping">{shipping === 0 ? "GRATIS" : fmtMXN(shipping)}</span>
        </div>
        <div className="flex justify-between items-baseline pt-3 mt-2 border-t border-zinc-200">
          <span className="text-zinc-900 font-semibold">{readOnly ? "Total pagado" : "Total"}</span>
          <span className="font-heading text-2xl font-extrabold text-zinc-900 numeric" data-testid="summary-total">
            {fmtMXN(totalMxn)}
            <span className="text-[10px] font-mono text-zinc-400 ml-1">MXN</span>
          </span>
        </div>
      </div>

      {/* Coupon input (only on non-compact summary to avoid duplicating on mobile) */}
      {!compact && !readOnly && setCouponInput && (
        <div className="mt-4 pt-4 border-t border-zinc-200">
          {!coupon ? (
            <div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyCoupon()}
                  placeholder="Código de cupón"
                  className="flex-1 h-10 px-3 text-sm bg-white border border-zinc-300 rounded-lg outline-none focus:border-zinc-900 font-mono uppercase"
                  data-testid="coupon-input"
                />
                <button
                  type="button"
                  onClick={applyCoupon}
                  disabled={couponLoading || !couponInput.trim()}
                  className="h-10 px-4 rounded-lg bg-zinc-100 hover:bg-zinc-200 disabled:opacity-50 text-xs font-semibold uppercase tracking-wider"
                  data-testid="apply-coupon"
                >
                  {couponLoading ? "…" : "Aplicar"}
                </button>
              </div>
              {couponError && <p className="mt-1.5 text-[11px] text-red-600" data-testid="coupon-error">{couponError}</p>}
            </div>
          ) : (
            <div className="flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-emerald-800">✓ Cupón <strong>{coupon.code}</strong> aplicado</span>
              <button onClick={removeCoupon} className="text-zinc-600 hover:text-red-600 underline">quitar</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ThreeDSChallenge({ result, onResolved }) {
  const [waiting, setWaiting] = useState(true);
  const stopRef = useRef(false);
  const resolvedRef = useRef(onResolved);
  useEffect(() => { resolvedRef.current = onResolved; }, [onResolved]);

  // El banco resuelve el challenge dentro del Status Screen Brick; nosotros
  // sincronizamos la orden con MP (refresh) hasta que deje de estar pendiente.
  useEffect(() => {
    stopRef.current = false;
    const started = Date.now();
    const poll = async () => {
      if (stopRef.current) return;
      try {
        const { data } = await axios.post(`${API}/api/public/checkout/order/${result.order_id}/refresh`);
        if (data.status && data.status !== "pending") {
          stopRef.current = true;
          setWaiting(false);
          resolvedRef.current(data);
          return;
        }
      } catch { /* reintenta */ }
      if (Date.now() - started < 10 * 60 * 1000) setTimeout(poll, 4000);
    };
    const t = setTimeout(poll, 6000);
    return () => { stopRef.current = true; clearTimeout(t); };
  }, [result.order_id]);

  return (
    <div className="bg-white border-2 border-[#009EE3]/40 rounded-2xl p-4" data-testid="three-ds-challenge">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={18} weight="duotone" className="text-[#009EE3]" />
        <h3 className="font-heading text-base font-extrabold tracking-tight text-zinc-900">Verificación de tu banco</h3>
      </div>
      <p className="text-xs text-zinc-600 mb-3">
        Tu banco necesita confirmar que eres tú. Completa la verificación aquí abajo (código por SMS o app del banco); no cierres esta página.
      </p>
      <StatusScreen
        initialization={{
          paymentId: String(result.payment_id),
          additionalInfo: {
            externalResourceURL: result.three_ds_info.external_resource_url,
            creq: result.three_ds_info.creq,
          },
        }}
        onError={(err) => console.error("StatusScreen 3DS error", err)}
      />
      {waiting && (
        <div className="mt-3 inline-flex items-center gap-2 text-[11px] text-zinc-500" data-testid="three-ds-waiting">
          <Spinner size={14} className="animate-spin" /> Esperando la confirmación del banco…
        </div>
      )}
    </div>
  );
}

function PaymentResultCard({ result, supportWhatsapp }) {
  const isPaid = result.status === "paid";
  const isPending = result.status === "pending";
  const helpMsg = encodeURIComponent(`Hola, intenté pagar en la tienda (pedido ${String(result.order_id || "").slice(0, 8)}) y mi pago fue rechazado. ¿Me pueden ayudar a completar la compra?`);
  const palette = isPaid
    ? { Icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200",
        title: "¡Pago aprobado!", sub: "Tu compra fue procesada con éxito. Te enviamos el recibo por correo." }
    : isPending
    ? { Icon: Clock, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200",
        title: "Pago en proceso", sub: result.voucher?.ticket_url
          ? "Tu referencia se generó. Paga antes de que expire para completar la orden."
          : "Mercado Pago está confirmando tu pago. Te avisaremos por correo cuando se acredite." }
    : { Icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-200",
        title: "El pago no se completó", sub: result.mp_status_detail_label || result.mp_status_detail || "Intenta de nuevo o usa otro método de pago." };
  const { Icon } = palette;
  return (
    <div className={`rounded-2xl border-2 ${palette.border} ${palette.bg} p-5`} data-testid={`result-card-${result.status}`}>
      <div className={`mx-auto w-12 h-12 rounded-full bg-white inline-flex items-center justify-center mb-3 ${palette.color}`}>
        <Icon size={24} weight="duotone" />
      </div>
      <h3 className="font-heading text-xl font-extrabold tracking-tight text-zinc-900">{palette.title}</h3>
      <p className="text-sm text-zinc-600 mt-1.5">{palette.sub}</p>
      {result.sale_folio && (
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-zinc-200 text-xs font-mono">
          Folio <span className="font-bold">#{result.sale_folio}</span>
        </div>
      )}
      {result.voucher?.ticket_url && (
        <a
          href={result.voucher.ticket_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 block w-full text-center h-11 leading-[2.75rem] rounded-full bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold"
          data-testid="voucher-link"
        >
          Ver mi referencia / código →
        </a>
      )}
      {!isPaid && !isPending && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 block w-full text-center h-11 leading-[2.75rem] rounded-full bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-semibold"
          data-testid="result-retry"
        >
          Intentar de nuevo
        </button>
      )}
      {!isPaid && !isPending && supportWhatsapp && (
        <a
          href={`https://wa.me/${supportWhatsapp}?text=${helpMsg}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center justify-center gap-2 w-full h-11 rounded-full bg-[#25D366] hover:opacity-90 text-white text-sm font-semibold"
          data-testid="result-help-whatsapp"
        >
          <WhatsappLogo size={18} weight="fill" /> Ayuda por WhatsApp para completar mi compra
        </a>
      )}
    </div>
  );
}
